import React, { useState, useEffect } from 'react';
import { styled } from '@mui/system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInfoCircle,
  faUserShield,
  faEnvelopeOpenText,
  faLock,
  faMobileAlt,
  faHistory,
  faServer,
} from '@fortawesome/free-solid-svg-icons';
import '../styles/enterprise-gateway.css';

const CTAButton = styled('a')({
  background: '#FFD000',
  color: '#000',
  border: 'none',
  borderRadius: 4,
  padding: '12px 24px',
  fontSize: '1rem',
  cursor: 'pointer',
  fontWeight: 600,
  transition: 'transform .1s ease',
  textDecoration: 'none',
  display: 'inline-block',
  '&:hover': { transform: 'scale(1.03)' },
});

export default function EnterpriseGatewayLandingPage() {
  const [showOverview, setShowOverview] = useState(false);

  const specs = [
    { icon: faUserShield,      title: 'Single Sign-On',     desc: 'Integrate seamlessly with Active Directory & LDAP.' },
    { icon: faEnvelopeOpenText, title: 'Data Messaging',    desc: 'Encrypted messaging queues with application-layer AES-256.' },
    { icon: faLock,            title: 'Data Encryption',     desc: 'AES-256, RSA-2048+, Diffie–Hellman key exchange.' },
    { icon: faMobileAlt,       title: 'Multi-Factor Auth',   desc: 'One-Time Passwords via Cloak Mobile Token.' },
    { icon: faHistory,         title: 'Audit & Tracking',    desc: 'Full trace logs for user and admin actions.' },
    { icon: faServer,          title: 'Reliability & Tools', desc: 'AWS-backed uptime and a web admin console.' },
  ];

  useEffect(() => {
    document.body.style.backgroundImage = "url('/cloak/public/img/cloak-bkg-l.jpeg')";
    document.body.style.backgroundSize = "cover";
    document.body.style.backgroundAttachment = "fixed";
    document.body.style.backgroundRepeat = "no-repeat";
    document.body.style.backgroundPosition = "center";

    return () => {
      document.body.style.backgroundImage = "/cloak/public/img/cloak-bkg-l.jpeg";
    };
  }, []);

  return (
    <div className="eg-page-container">

      {/* Hero with Info Icon */}
      <section className="eg-hero">
        <div className="eg-hero-header">
          <h1>Enterprise Gateway</h1>
          <FontAwesomeIcon
            icon={faInfoCircle}
            className="eg-info-icon"
            onClick={() => setShowOverview(true)}
          />
        </div>
        <p className="eg-subhead">
          One unified gateway for SSO, MFA, encryption, and audit—powered by CloakApps.
        </p>
        <CTAButton
          href="https://www.cloakapps.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          Get Started with Gateway
        </CTAButton>
      </section>

      {/* Features Grid */}
      <section className="eg-features">
        {specs.map((f, i) => (
          <div key={i} className="eg-feature-card">
            <FontAwesomeIcon icon={f.icon} className="eg-feature-icon" />
            <h3>{f.title}</h3>
            <p>{f.desc}</p>
          </div>
        ))}
      </section>

      {/* Overview Modal */}
      {showOverview && (
        <div className="eg-modal-overlay" onClick={() => setShowOverview(false)}>
          <div className="eg-modal" onClick={e => e.stopPropagation()}>
            <button
              className="eg-modal-close"
              onClick={() => setShowOverview(false)}
            >×</button>
            <h2>What is the Enterprise Gateway?</h2>
            <p>
              The CloakApps Enterprise Gateway is a single, unified entry point that delivers
              seamless Single-Sign-On, strong multi-factor authentication, end-to-end encryption,
              and full audit trails—all managed from a central console. It ties your existing
              directory services and security policies into one scalable, highly available service,
              so you can protect every connection—on premises or in the cloud—with minimal overhead.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
